import "./AutocompleteMultiSelect.css";

import RCSelect from "rc-select";
import React, { ReactNode } from "react";

import { DropdownLightIcon } from "../../../assets";
import { SelectData } from "../../../models";
import { FormLabel } from "../FormLabel";
import { getDropdownPopupContainer } from "../utils/getDropdownPopupContainer";
import { useAutocompleteMultiSelect } from "./useAutocompleteMultiSelect";

interface SelectionItemTagProps {
  value: string;
  label: ReactNode;
  data: SelectData;
  activeValue: string | null;
  onTagMouseDown: (value: string, event: React.MouseEvent) => void;
}

const SelectionItemTag = ({ value, label, data, activeValue, onTagMouseDown }: SelectionItemTagProps): JSX.Element => {
  // Prefer the current data label; fall back to rc-select's cached tag label (which survives
  // server-side search replacing `data` with a filtered page that no longer contains this
  // value) before finally falling back to the raw value.
  const text = data.find((d) => `${d.key}` === value)?.value ?? label ?? value;
  const isActive = activeValue === value;

  return (
    <span
      role="button"
      tabIndex={-1}
      className={`AutocompleteMultiSelect-selection-item-content${
        isActive ? " AutocompleteMultiSelect-selection-item-active" : ""
      }`}
      onMouseDown={(event) => onTagMouseDown(value, event)}
    >
      {text}
    </span>
  );
};

export interface AutocompleteMultiSelectProps {
  label?: string;
  allowLabelDangerousHtml?: boolean;
  initialKey?: string[] | null;
  values?: string[] | null;
  placeholder?: string;
  className?: string;
  dropdownMatchSelectWidth?: boolean;
  data: SelectData;
  order?: string[];
  onChange: (values: string[]) => void;
  onTypeSearch?: (value?: string) => void;
  tooltip?: ReactNode;
  overrideOpen?: boolean;
  overrideOnDropdownVisibleChange?: (open: boolean) => void;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

export const AutocompleteMultiSelect = ({
  label,
  allowLabelDangerousHtml,
  initialKey,
  values,
  placeholder = "Please select",
  className,
  dropdownMatchSelectWidth,
  data,
  order,
  onChange,
  onTypeSearch,
  tooltip,
  overrideOpen,
  overrideOnDropdownVisibleChange,
  getPopupContainer,
}: AutocompleteMultiSelectProps): JSX.Element => {
  const { searchValue, selectedValues, activeValue, onChangeInternal, onTagMouseDown, rootRef, handleSearch, options } =
    useAutocompleteMultiSelect({
      values,
      data,
      order,
      onChange,
      onTypeSearch,
    });

  return (
    <FormLabel label={label} tooltip={tooltip} tooltipJustify="left" allowDangerousHtml={allowLabelDangerousHtml}>
      <div
        ref={rootRef}
        className={`${className || ""} ${onTypeSearch !== undefined ? "Search" : ""} ${
          searchValue ? "Searching" : ""
        } form_field`}
      >
        {placeholder && !searchValue && (onTypeSearch !== undefined || !selectedValues?.length) && (
          <p className="body1 AutocompleteMultiSelectPlaceholder">{placeholder}</p>
        )}
        <RCSelect
          open={overrideOpen}
          onDropdownVisibleChange={overrideOnDropdownVisibleChange}
          mode="multiple"
          defaultValue={initialKey}
          searchValue={searchValue}
          value={selectedValues}
          suffixIcon={<DropdownLightIcon />}
          onChange={onChangeInternal}
          onSearch={handleSearch}
          prefixCls="AutocompleteMultiSelect"
          autoClearSearchValue={false}
          showSearch
          filterOption={false}
          notFoundContent={null}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={getPopupContainer ?? getDropdownPopupContainer}
          dropdownAlign={{ overflow: { adjustY: 0 } }}
          // eslint-disable-next-line react/no-unstable-nested-components
          tagRender={({ value, label: tagLabel }) => (
            <SelectionItemTag
              value={`${value}`}
              label={tagLabel}
              data={data}
              activeValue={activeValue}
              onTagMouseDown={onTagMouseDown}
            />
          )}
          removeIcon
        >
          {options}
        </RCSelect>
      </div>
    </FormLabel>
  );
};
